import { API } from "aws-amplify";
import { apiGateway } from "../../config";
import axios from "axios";
import {
  UPDATE_DOCUMENT_PROPS,
  ADD_DOCUMENTS,
  DELETE_DOCUMENT,
  CREATING_DOCUMENT,
  UPDATE_DOCUMENT_USAGE,
  FETCHING_DOCUMENTS,
  FETCHED_DOCUMENTS,
  ADD_DOCUMENT,
  UPDATE_SINGLE_DOCUMENT,
  UPDATING_DOCUMENT,
  DELETE_DOCUMENT_BY_TAG,
  APPEND_DOCUMENTS
} from "./constants";
import { store } from "..";
import { showNotification } from "./notification";
import { getProject } from "./projects";
import { adminCreateShortLink } from './admin';
import moment from 'moment';
import { getMergeMessage } from '../../utilities';
const Gateway = apiGateway.find((gateway) => gateway.name === "client");

// In-flight tracker to prevent redundant parallel loads
const inflightDocs = new Map(); // key -> Promise
export const getDocuments = (project_id, override = false, { limit, cursor, include_fasten = false, account_id = null } = {}) => async (dispatch) => {
  const state = store.getState();
  const fetched = state.documents.fetched[project_id];
  const fetching = state.documents.fetching;
  const key = `${project_id || 'all'}:${cursor || 'first'}:${limit || 'default'}`;
  if (inflightDocs.has(key)) return inflightDocs.get(key);
  if ((!fetching && !fetched) || override || cursor) {
    dispatch({ type: FETCHING_DOCUMENTS, payload: true });
    const qs = new URLSearchParams();
    if (limit) qs.set('limit', limit);
    if (cursor) qs.set('cursor', cursor);
    if (include_fasten) qs.set('include_fasten', 'true');
    const targetAccountId = account_id || state.session.account.account_id;
    const path = `/documents/get-all/${project_id}/${targetAccountId}/${state.authentication.user.cognito_id}${qs.toString() ? `?${qs.toString()}` : ''}`;
    const p = API.get(
      Gateway.name,
      path,
      {
        headers: {
          "Authorization": state.session.token
        }
      })
      .then(async (data) => {
        // If cursor provided, append; else replace
        const list = data.payload || [];
        if (cursor) {
          const existing = state.documents.list[project_id] || [];
            dispatch({ type: ADD_DOCUMENTS, payload: { list: [...existing, ...list], project_id } });
        } else {
          dispatch({ type: ADD_DOCUMENTS, payload: { list, project_id } });
        }
        dispatch({ type: UPDATE_DOCUMENT_USAGE, payload: { project_id, usage: data?.usage } });
        if (!cursor) dispatch({ type: FETCHED_DOCUMENTS, payload: { status: true, project_id } });
        dispatch({ type: FETCHING_DOCUMENTS, payload: false });
        inflightDocs.delete(key);
        return data;
      })
      .catch((error) => {
        if (error?.response?.data?.payload && !error.response.data?.payload.length && !cursor) dispatch({ type: ADD_DOCUMENTS, payload: { list: [], project_id } });
        dispatch({ type: UPDATE_DOCUMENT_USAGE, payload: { project_id, usage: error.response?.data?.usage } });
        if (!cursor) dispatch({ type: FETCHED_DOCUMENTS, payload: { status: true, project_id } });
        dispatch({ type: FETCHING_DOCUMENTS, payload: false });
        inflightDocs.delete(key);
        return {
          success: false,
          error
        };
      });
    inflightDocs.set(key, p);
    return p;
  }
  return { skipped: true };
};

export const createMultipleDocuments = async (files) => {
  let docs = [];
  store.dispatch({ type: CREATING_DOCUMENT, payload: true });
  for (let file of files) {
    file.config.filename = file.config.filename.replaceAll("/", "-");
    const doc = await API.post(
      Gateway.name,
      `/documents/create/${(store.getState().session.account.account_id)}/${store.getState().authentication.user.cognito_id}`,
      {
        headers: {
          "Authorization": store.getState().session.token
        },
        body: {
          file: file.config
        }
      });
      if (doc.success) {
        const signed = await getSignedURLRaw(file);
        if (signed.success) file.signed_url = signed.payload;
        let headers = {
          'Content-Type': file.file.type
        };
        const document = await axios.put(file.signed_url, file.file, {
          disableMultipart: true,
          headers
        });
        if (document.status === 200) docs.push(doc.payload);
      }
  }
  if (docs.length === files.length) {
    store.dispatch({ type: APPEND_DOCUMENTS, payload: { list: docs, project_id: docs[0].project_id } });
    return { success: true };
  }
  store.dispatch({ type: CREATING_DOCUMENT, payload: false });
  store.dispatch(showNotification("error", "Some documents failed to upload."));
  return { success: false, error: "Some documents failed to upload." };
};

export const createDocument = (url, file, silent = false, refreshProject = true, shouldCreateRecord, share = false) => async (dispatch) => {
  dispatch({ type: CREATING_DOCUMENT, payload: true });
  let headers = {
    'Content-Type': file.file.type
  };
  if (share) headers["x-amz-tagging"] = "temporary=1" 
  const document = await axios.put(url, file.file, {
    disableMultipart: true,
    headers
  });

  if (document.status === 200) {
    if (shouldCreateRecord) {
      const isAdminCreate = file.config._admin_create;
      const endpoint = isAdminCreate 
        ? `/create-document/${store.getState().authentication.user.cognito_id}`
        : `/documents/create/${(store.getState().session.account.account_id)}/${store.getState().authentication.user.cognito_id}`;
      
      // Clean up admin flags
      delete file.config._admin_create;
      
      return API.post(
        Gateway.name,
        endpoint,
        {
          headers: {
            "Authorization": store.getState().session.token
          },
          body: {
            file: file.config
          }
        })
        .then(async (data) => {
          dispatch({ type: ADD_DOCUMENT, payload: data.payload });
          dispatch({ type: UPDATE_DOCUMENT_USAGE, payload: { project_id: file.config.project_id, usage: data.payload.size + (store.getState().documents.usage[file.config.project_id] || 0) } });
          dispatch({ type: CREATING_DOCUMENT, payload: false });
          if (!silent) dispatch(showNotification("success", "Document created successfully."));
          if (refreshProject && file.config.project_id) dispatch(getProject(file.config.project_id, true));
          return data;
        })
        .catch((error) => {
          dispatch(showNotification("error", error.message));
          dispatch({ type: CREATING_DOCUMENT, payload: false });
          return {
            success: false,
            error
          };
        });
    } else {
      dispatch({ type: CREATING_DOCUMENT, payload: false });
      return { success: true };
    }
  } else {
    dispatch(showNotification("error", "Could not upload document. Please try again."));
    dispatch({ type: CREATING_DOCUMENT, payload: false });
    return { success: false };
  }
};

export const getSignedURL = (file, method = "put", duration, silent = false, refreshProject, shouldCreateRecord = true) => async (dispatch) => {
  if (method === "put") {
    if (shouldCreateRecord) dispatch({ type: CREATING_DOCUMENT, payload: true });
    file.config.filename = file.config.filename.replaceAll("/", "-");
  }
  
  // Allow admin to override account_id for uploads to other accounts
  const accountId = file.config._admin_account_override || store.getState().session.account.account_id;
  const cleanConfig = { ...file.config };
  delete cleanConfig._admin_account_override; // Remove from config sent to backend
  
  // Also remove from original file.config before passing to createDocument
  delete file.config._admin_account_override;
  
  return API.post(
    Gateway.name,
    `/documents/get-signed-url/${accountId}/${store.getState().authentication.user.cognito_id}`,
    {
      headers: {
        "Authorization": store.getState().session.token
      },
      body: {
        config: { ...cleanConfig, ...(cleanConfig.key_override ? { key: cleanConfig.key_override } : {}), duration },
        method
      }
    })
    .then(async (data) => {
      if (data.success) {
        if (method === "put") return dispatch(createDocument(data.payload, file, silent, refreshProject, shouldCreateRecord, file.config.share));
        dispatch({ type: CREATING_DOCUMENT, payload: false });
        return data.payload;
      }
      dispatch({ type: CREATING_DOCUMENT, payload: false });
      return { success: false };
    })
    .catch((error) => {
      dispatch({ type: CREATING_DOCUMENT, payload: false });
      return {
        success: false,
        error
      };
    });
};

const getSignedURLRaw = (file, duration) => {
  return API.post(
    Gateway.name,
    `/documents/get-signed-url/${(store.getState().session.account.account_id)}/${store.getState().authentication.user.cognito_id}`,
    {
      headers: {
        "Authorization": store.getState().session.token
      },
      body: {
        config: { ...file.config, duration },
        method: "put"
      }
    })
}

export const getDocument = (key, project_id) => async (dispatch) => {
  return API.post(
    Gateway.name,
    `/documents/get-document/${project_id}/${(store.getState().session.account.account_id)}/${store.getState().authentication.user.cognito_id}`,
    {
      headers: {
        "Authorization": store.getState().session.token
      },
      body: {
        key
      }
    })
    .then(async (data) => {
      return data.payload;
    })
    .catch((error) => {
      return { success: false, error };
    });
};

export const updateDocument = (project_id, id, updates, new_file, accountIdOverride = null) => async (dispatch) => {
  dispatch({ type: UPDATING_DOCUMENT, payload: true });
  const accountId = accountIdOverride || store.getState().session.account.account_id;
  return API.patch(
    Gateway.name,
    `/documents/update/${project_id}/${id}/${accountId}/${store.getState().authentication.user.cognito_id}`,
    {
      headers: {
        Authorization: store.getState().session.token
      },
      body: {
        new_file,
        updates
      }
    }).then((data) => {
      dispatch(showNotification("success", "Document updated successfully."));
      dispatch({ type: UPDATE_SINGLE_DOCUMENT, payload: data.payload });
      return data;
    }).catch((error) => {
      dispatch(showNotification("error", error.message));
      dispatch({ type: UPDATING_DOCUMENT, payload: false });
      return {
        success: false,
        error
      };
    });
};

export const deleteDocument = (key, project_id, size) => async (dispatch) => {
  return API.del(
    Gateway.name,
    `/documents/delete/${project_id}/${encodeURIComponent(key)}/${(store.getState().session.account.account_id)}/${store.getState().authentication.user.cognito_id}`,
    {
      headers: {
        "Authorization": store.getState().session.token
      }
    })
    .then(async (data) => {
      dispatch({ type: DELETE_DOCUMENT, payload: { project_id, filename: key, size } });
      dispatch(showNotification("success", "Document deleted successfully."));
      return data;
    })
    .catch((error) => {
      dispatch(showNotification("error", error.message));
      return {
        success: false,
        error
      };
    });
};

export const deleteDocumentsByTag = (tag, project_id) => async (dispatch) => {
  return API.patch(
    Gateway.name,
    `/documents/delete-by-tag/${project_id}/${(store.getState().session.account.account_id)}/${store.getState().authentication.user.cognito_id}`,
    {
      headers: {
        "Authorization": store.getState().session.token
      },
      body: {
        question_name: tag
      }
    })
    .then(async (data) => {
      dispatch({ type: DELETE_DOCUMENT_BY_TAG, payload: { project_id, tag, size: data.payload.size } });
      dispatch(showNotification("success", "Document deleted successfully."));
      return data;
    })
    .catch((error) => {
      dispatch(showNotification("error", error.message));
      return {
        success: false,
        error
      };
    });
};

export const getPreview = async (key, project_id, size = "thumbnail") => {
  // For static documents without project_id, use 'null' as placeholder
  const projectIdParam = project_id !== null && project_id !== undefined ? project_id : 'null';
  return API.get(
    Gateway.name,
    `/documents/get-preview/${size}/${store.getState().session.account.account_id}/${store.getState().authentication.user.cognito_id}/${projectIdParam}/${key}`,
    {
      headers: {
        "Authorization": store.getState().session.token
      }
    })
};
export const updateDocumentProps = (project_id, key, updates) => async (dispatch) => {
  dispatch({ type: UPDATE_DOCUMENT_PROPS, payload: { project_id, key, updates } });
};

export const reprocessDocument = (document_id) => async (dispatch) => {
  return API.get(
    Gateway.name,
    `/documents/reprocess-document/${document_id}/${store.getState().session.account.account_id}/${store.getState().authentication.user.cognito_id}`,
    {
      headers: {
        "Authorization": store.getState().session.token
      }
    })
    .then(async (data) => {
      dispatch(showNotification("success", "Document reprocessing queued successfully."));
      dispatch({ type: UPDATE_SINGLE_DOCUMENT, payload: data.payload });
      return data;
    })
    .catch((error) => {
      dispatch(showNotification("error", error.response && error.response.data ? error.response.data.message : "Something went wrong."));
      return {
        success: false,
        error
      };
    });
};

export const fillPdfForm = (project_id, filename, custom_instructions = '', form_name = '') => async (dispatch) => {
  const state = store.getState();
  return API.post(
    Gateway.name,
    `/documents/fill-form/${project_id}/${state.session.account.account_id}/${state.authentication.user.cognito_id}`,
    {
      headers: {
        Authorization: state.session.token
      },
      body: {
        filename,
        custom_instructions,
        form_name
      }
    })
    .then((data) => {
      if (data.processing) {
        // Async processing - return job info for Ably subscription
        dispatch(showNotification("info", "Processing Form", "Your form is being filled. This typically takes less than a minute. You'll be notified when complete."));
        return data;
      } else if (data.success && data.payload) {
        // Legacy synchronous response (shouldn't happen but handle it)
        dispatch({ type: ADD_DOCUMENT, payload: data.payload });
        dispatch(showNotification("success", "Form Filled Successfully", "Your filled form has been saved and is ready to download."));
        return data;
      }
      return data;
    })
    .catch((error) => {
      dispatch(showNotification("error", "Form Fill Failed", error.message || "An error occurred while filling the form."));
      return { success: false, error };
    });
};

// Short-link based document sharing (replaces legacy getSignedURL share branch gradually)
export const createDocumentShareLink = async ({ document, expiresIn, maxUses = 1, shareCognito, message, durationLabel }) => {
  if (!document) return { success:false, message:'document required' };
  const project_id = document.project_id;
  // Merge tokens client-side using existing getMergeMessage utility (mirrors legacy getSignedURL share flow behavior)
  try {
    if (message) {
      const state = store.getState();
      const sharer = state?.users?.list?.find((u) => shareCognito === u.cognito_id) || {};
      const now = Date.now();
      const expirationMs = expiresIn ? now + (expiresIn * 1000) : null;
      // Create date object from UTC timestamp and format in local time
      const expiration_date = expirationMs ? moment(expirationMs).local().format("MMMM DD, YYYY [at] h:mm A") : null;
      const mergedContext = { ...document, ...sharer, expiration_time: durationLabel, expiration_date };
      message = getMergeMessage(message, mergedContext);
    }
  } catch (e) { /* silent */ }
  const res = await adminCreateShortLink({ project_id, target_type:'document', target_id: document.id, expires_in: expiresIn, max_uses: maxUses, share_cognito: shareCognito, message, duration_label: durationLabel });
  if (res?.success && res?.payload?.short_url) return { success:true, url: res.payload.short_url, expires_at: res.payload.expires_at, max_uses: res.payload.max_uses, email_sent: res.payload.email_sent };
  return res || { success:false, message:'Failed to create share link' };
};